import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  TextInput,
  Radio,
  Group,
  Button,
  SimpleGrid,
  Checkbox,
  Flex,
  Text,
  Box,
} from '@mantine/core';
import '@mantine/core/styles.css';
import ReCAPTCHA from 'react-google-recaptcha';
import apiService from '../../../services/ApiService';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import DateOfBirthInput from '../../../components/common/DateOfBirthInput';
import Header from '../../../components/layout/Header';

interface Varg {
  vargId: number;
  vargCode: string;
  vargName: string;
  type: string;
  redirectToParichay: boolean;
  university: string | null;
  updatedDt: number;
  updatedBy: number;
  active: boolean;
  paymentRequired: boolean;
  kendraRequired: boolean;
  documentRequired: boolean;
}

interface Props {
  varg: Varg;
}

export default function VargForm(props: Props) {
  const initialFormData = {
    varg: { vargCode: props.varg.vargCode },
    gender: 'M',
    firstName: '',
    middleName: '',
    lastName: '',
    address: '',
    pinCd: '',
    city: '',
    country: '',
    mobileNbr: '',
    email: '',
    eduQualification: '',
    occupation: '',
    occuLocationDesig: '',
    dob: '',
    recaptchaResponse: '',
    ailments: '',
  };
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const meta =
    'Nirogatva Varg is a one-month In-person yogasan varga conducted by Janardanswami Yogabhyasi Mandal, Nagpur. Held every morning from 6:00 to 7:30 am from the 4th of every month.';
  // Callback function that will be called when the reCAPTCHA is solved
  const onRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  const handleDateChange = (value: string | null) => {
    setFormData((prev) => ({
      ...prev,
      dob: value || '', // Store date as string in DD/MM/YYYY format
    }));
  };

  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  // Success callback to handle response
  const handleSuccess = (responseData: any) => {
    console.log('Success!', responseData);
    alert('Data submitted successfully!');
    setFormData(initialFormData);
    setLoading(false);
  };

  // Failure callback to handle errors
  const handleFailure = (error: any) => {
    console.error('Error!', error);
    alert('There was an error processing the request.');
    setLoading(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (props.varg?.vargName) {
      document.title = props.varg.vargName;
    }
  }, [props.varg.vargName]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!recaptchaValue) {
      alert('Please complete the CAPTCHA');
      return;
    }
    if (!isPhoneNumberValid) {
      // If phone number is invalid, prevent form submission
      return;
    }

    setLoading(true);

    console.log('Submitted Data:', formData);
    formData.recaptchaResponse = recaptchaValue;
    // Call the API service to perform the POST request
    await apiService.request(
      'post', // HTTP method
      'jsyogvarg', // URL
      formData, // Request payload (form data)
      {}, // Optional config like headers
      handleSuccess, // onSuccess callback
      handleFailure // onFailure callback
    );
  };

  return (
    <>
      <Header />
      <Box bg="#942780" py="sm">
        <Text ta="center" c="white" fw={500}>
          Register now to join the {props.varg.vargName}
        </Text>
      </Box>
      <Helmet>
        <meta
          property="og:title"
          content={`${props.varg.vargName} - JANARDANSWAMI YOGABHYASI MANDAL, NAGPUR`}
        />
        <meta property="og:description" content={meta} />
        <meta property="og:site_name" content="JANARDANSWAMI YOGABHYASI MANDAL, NAGPUR" />
      </Helmet>
      <form onSubmit={handleSubmit}>
        <>
          <Flex mt={80} px={{ base: 20, sm: 50, md: 100, lg: 150 }}>
            <SimpleGrid cols={1} spacing="md">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Radio.Group
                  label="Varg"
                  value={formData.varg.vargCode} // string value for the selected radio
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      varg: { vargCode: value }, // store it back as an object
                    }))
                  }
                  required
                >
                  <Radio value={props.varg.vargCode} label={props.varg.vargName} />
                </Radio.Group>

                <Radio.Group
                  label="Gender"
                  name="gender"
                  value={formData.gender}
                  onChange={(value) => setFormData((prev) => ({ ...prev, gender: value }))}
                  mb="md"
                  required
                >
                  <Group>
                    <Radio value="F" label="Female" />
                    <Radio value="M" label="Male" />
                  </Group>
                </Radio.Group>
              </SimpleGrid>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                <TextInput
                  label="First Name"
                  placeholder="Enter First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <TextInput
                  label="Middle Name"
                  placeholder="Enter Middle Name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Enter Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </SimpleGrid>

              <TextInput
                label="Complete Address"
                placeholder="Enter Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                mt="sm"
                mb="sm"
              />

              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                <TextInput
                  label="Pincode"
                  placeholder="Enter Pincode"
                  name="pinCd"
                  type="number"
                  maxLength={6}
                  value={formData.pinCd}
                  onChange={handleChange}
                />
                <TextInput
                  label="City"
                  placeholder="Enter City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
                <TextInput
                  label="Country"
                  placeholder="Enter Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                <PhoneNumberInput
                  label="Whatsapp Number"
                  name="mobileNbr"
                  value={formData.mobileNbr}
                  onChange={handleChange}
                  required
                  setValid={setIsPhoneNumberValid} // Pass the callback to validate phone number
                />

                <TextInput
                  label="Email Address"
                  placeholder="Enter Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <DateOfBirthInput
                  label="Date of Birth"
                  name="dob"
                  value={formData.dob} // Pass the value as string (DD/MM/YYYY)
                  onChange={handleDateChange}
                  required
                  placeholder="Enter your date of birth" // Custom placeholder text
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
                <TextInput
                  label="Educational Qualifications"
                  placeholder="Enter Qualifications"
                  name="eduQualification"
                  value={formData.eduQualification}
                  onChange={handleChange}
                />
                <TextInput
                  label="Occupation"
                  placeholder="Enter Occupation"
                  name="occupation"
                  value={formData.occupation}
                  onChange={handleChange}
                />
                <TextInput
                  label="Location of Occupation & Designation"
                  placeholder="Enter Location"
                  name="occuLocationDesig"
                  value={formData.occuLocationDesig}
                  onChange={handleChange}
                  mb="md"
                />
              </SimpleGrid>
              <TextInput
                label="If suffering from Ailments, diseases or chronic pain"
                name="ailments"
                value={formData.ailments}
                onChange={handleChange}
              />
              <span>Rules*</span>
              <Checkbox
                size="xs"
                label="By checking this option in this online form, I agree that the information submitted by me is correct and complete. I also agree to attend the classes on all days and sit through the entire duration of each class as well as follows the rules of Janardanswami Yogabhyasi Mandal, Nagpur. I also pledge that, after completing this varg, I shall practice Yog-Asan daily and incessantly and that I will promote and teach Yog absolutely FREE of cost to the best of my abilities."
                required
              />
              <span>Consent to send information*</span>
              <Checkbox
                size="xs"
                label="By checking this option in this online form, I agree to add my above-mentioned email ID as well as phone number to Janardanswami Yogabhyasi Mandal's mailing & SMS broadcast lists, respectively and grant your non-profit organisation Janardanswami Yogabhyasi Mandal, Nagpur the right to send me regular Newsletters or other communication pertaining to the activities of the organisation."
                required
              />
              <span>Consent to send information*</span>
              <ReCAPTCHA
                sitekey="6Ldd5PwcAAAAAEI9aL8oxc3cZmKVtTUXfGvRKyBY" // Replace with your site key
                onChange={onRecaptchaChange}
              />
            </SimpleGrid>
          </Flex>
          <Button
            ml={{ base: 20, sm: 50, md: 100, lg: 150 }}
            mt={50}
            type="submit"
            disabled={loading}
            bg="#942780"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </>
      </form>
    </>
  );
}
